import PeopleOutlineRoundedIcon from "@mui/icons-material/PeopleOutlineRounded";
import Box from "@mui/joy/Box";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import DialogTitle from "@mui/joy/DialogTitle";
import Grid from "@mui/joy/Grid";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalOverflow from "@mui/joy/ModalOverflow";
import Typography from "@mui/joy/Typography";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { OmopDbReport } from "../../../models/analytics";
import StatisticsTable from "./StatisticsTable";
import DistributionTable from "./DistributionTable";

interface AnalyticsModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly dbReport: OmopDbReport | null;
}

export default function AnalyticsModal({ isOpen, onClose, dbReport }: AnalyticsModalProps) {
  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalOverflow>
        <ModalDialog layout="fullscreen">
          <ModalClose />
          <DialogTitle level="h2">Analytics</DialogTitle>
          {dbReport && (
            <Grid
              container
              direction="row"
              spacing={4}
              sx={{
                justifyContent: "flex-start",
                alignItems: "flex-start",
              }}
            >
              {dbReport.statistics_data && (
                <Grid md={4} xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        Baseline characteristics
                      </Typography>
                      <StatisticsTable data={dbReport.statistics_data} />
                    </CardContent>
                  </Card>
                </Grid>
              )}
              <Grid container md={8} xs={12}>
                <Grid md={6} sm={6} xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        Number of patients
                      </Typography>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ height: "250px" }}
                      >
                        <PeopleOutlineRoundedIcon fontSize="large" />
                        <Typography level="title-lg" sx={{ mt: 2 }}>
                          {dbReport.number_of_patient} patient
                          {dbReport.number_of_patient > 1 && "s"}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid md={6} sm={6} xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        Gender distribution
                      </Typography>
                      <Box sx={{ height: "250px" }}>
                        <PieChart
                          series={[
                            {
                              data: dbReport.gender_distribution,
                            },
                          ]}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {dbReport.year_of_birth_distribution?.xData &&
                  dbReport.year_of_birth_distribution?.serieData && (
                    <Grid md={6} xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography level="title-md" sx={{ mb: 2 }}>
                            Year of birth distribution
                          </Typography>
                          <Box sx={{ height: "250px" }}>
                            <BarChart
                              xAxis={[
                                {
                                  scaleType: "band",
                                  data: dbReport.year_of_birth_distribution.xData,
                                },
                              ]}
                              series={[{ data: dbReport.year_of_birth_distribution.serieData }]}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
              </Grid>

              {dbReport.cancer_distribution && (
                <Grid md={4} xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        Cancer distribution - Top 20
                      </Typography>
                      <DistributionTable
                        firstColumnName="Cancer name"
                        data={dbReport.cancer_distribution}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {dbReport.condition_distribution && (
                <Grid md={4} xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        Condition distribution (excl. cancer) - Top 20
                      </Typography>
                      <DistributionTable
                        firstColumnName="Condition name"
                        data={dbReport.condition_distribution}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {dbReport.drug_distribution && (
                <Grid md={4} xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        Drug distribution - Top 20
                      </Typography>
                      <DistributionTable
                        firstColumnName="Drug name"
                        data={dbReport.drug_distribution}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {dbReport.procedure_distribution && (
                <Grid md={4} xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        Procedure distribution - Top 20
                      </Typography>
                      <DistributionTable
                        firstColumnName="Procedure name"
                        data={dbReport.procedure_distribution}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {dbReport.observation_distribution && (
                <Grid md={4} xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        Observation distribution - Top 20
                      </Typography>
                      <DistributionTable
                        firstColumnName="Observation name"
                        data={dbReport.observation_distribution}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
}
